import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Trash2, Plus, ArrowLeft, Loader2, Upload, Settings2, ListChecks, Users, Palette, Eye, GripVertical, ChevronDown, GitBranch, Clock, X } from 'lucide-react';
import { toast } from 'sonner';
import { QuestionField, isVisible, fileUrl, SpecifyInputs } from './SurveyFillPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_ACCENT = '#E60000';
const THEME_PRESETS = ['#E60000', '#4F46E5', '#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0F172A'];
const BG_PRESETS = ['#FFFFFF', '#F9FAFB', '#FEF2F2', '#EFF6FF', '#ECFDF5', '#FFF7ED', '#F5F3FF', '#0F172A'];

const FIELD_TYPES = [
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text (paragraph)' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'single_choice', label: 'Multiple Choice — Single' },
  { value: 'multi_choice', label: 'Multiple Choice — Checkboxes' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'rating_5', label: 'Star Rating (1–5)' },
  { value: 'star_10', label: 'Star Rating (1–10)' },
  { value: 'likert_matrix', label: 'Likert Matrix (single per row)' },
  { value: 'likert_matrix_multi', label: 'Likert Matrix (multi per row)' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'file', label: 'File Upload (image/pdf/xlsx)' },
  { value: 'section', label: 'Section Header' },
];
const NEEDS_OPTIONS = new Set(['single_choice', 'multi_choice', 'dropdown', 'likert_matrix', 'likert_matrix_multi']);
const NEEDS_ROWS = new Set(['likert_matrix', 'likert_matrix_multi']);
const CHOICE_TYPES = new Set(['single_choice', 'dropdown', 'yes_no', 'multi_choice']);

// When departments/locations/entities are chosen, restrict the employee picker to matches
export function filterEmployees(emps, audience) {
  const d = audience.departments || [], l = audience.locations || [], en = audience.entities || [];
  if (!d.length && !l.length && !en.length) return emps;
  return emps.filter(e =>
    (!d.length || d.includes(e.department)) &&
    (!l.length || l.includes(e.location)) &&
    (!en.length || en.includes(e.entity))
  );
}

const emptyQ = (type = 'short_text') => ({
  id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  type, label: '', description: '', required: false,
  options: NEEDS_OPTIONS.has(type) ? ['Option 1', 'Option 2'] : undefined,
  rows: NEEDS_ROWS.has(type) ? ['Row 1', 'Row 2'] : undefined,
  condition: null,
});

const TABS = [
  { value: 'details', label: 'Details', icon: Settings2 },
  { value: 'questions', label: 'Questions', icon: ListChecks },
  { value: 'audience', label: 'Audience', icon: Users },
  { value: 'branding', label: 'Branding', icon: Palette },
  { value: 'preview', label: 'Preview', icon: Eye },
];

export default function SurveyBuilderPage() {
  const { getAuthHeader, sessionToken } = useAuth();
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const isEdit = Boolean(surveyId);

  const [tab, setTab] = useState('details');
  const [audienceOpts, setAudienceOpts] = useState({ departments: [], locations: [], employees: [] });
  const [survey, setSurvey] = useState({
    title: '', description: '',
    questions: [emptyQ('short_text')],
    audience: { entire_org: false, departments: [], locations: [], entities: [], grades: [], employee_ids: [] },
    is_anonymous: false, is_mandatory: false,
    visibility: 'Department',
    scheduled_at: '',
    thank_you_message: 'Thank you for your feedback!',
    theme_color: DEFAULT_ACCENT, background_color: '#F9FAFB', logo_url: '',
    sender_department: 'Admin',
  });
  const [saving, setSaving] = useState(false);
  const accent = survey.theme_color || DEFAULT_ACCENT;

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/surveys/audience-options`, { headers: getAuthHeader() });
        if (r.ok) setAudienceOpts(await r.json());
      } catch { /* ignore */ }
      if (isEdit) {
        const r2 = await fetch(`${API}/surveys/${surveyId}`, { headers: getAuthHeader() });
        if (r2.ok) {
          const s = await r2.json();
          setSurvey(prev => ({ ...prev, ...s, visibility: 'Department', audience: s.audience || prev.audience, theme_color: s.theme_color || DEFAULT_ACCENT, background_color: s.background_color || '#F9FAFB' }));
        }
      }
    })();
  }, [getAuthHeader, isEdit, surveyId]);

  const setQuestions = (qs) => setSurvey(s => ({ ...s, questions: qs }));
  const updateQ = (id, patch) => setSurvey(s => ({ ...s, questions: s.questions.map(q => q.id === id ? { ...q, ...patch } : q) }));
  const removeQ = (id) => setSurvey(s => ({ ...s, questions: s.questions.filter(q => q.id !== id) }));
  const addQ = (type) => setSurvey(s => ({ ...s, questions: [...s.questions, emptyQ(type)] }));

  const setAudienceList = (key, list) => setSurvey(s => ({ ...s, audience: { ...s.audience, [key]: list } }));

  const save = async (publishAfter = false) => {
    if (!survey.title.trim()) { toast.error('Title required'); setTab('details'); return; }
    if (!survey.questions.length) { toast.error('Add at least one question'); setTab('questions'); return; }
    setSaving(true);
    try {
      const payload = { ...survey, visibility: 'Department' };
      payload.questions = payload.questions.map(q => ({
        ...q,
        options: NEEDS_OPTIONS.has(q.type) ? (q.options || []).map(o => o.trim()).filter(Boolean) : undefined,
        rows: NEEDS_ROWS.has(q.type) ? (q.rows || []).map(o => o.trim()).filter(Boolean) : q.rows,
      }));
      const url = isEdit ? `${API}/surveys/${surveyId}` : `${API}/surveys`;
      const method = isEdit ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Save failed'); return; }
      const id = d.survey_id || surveyId;
      toast.success(isEdit ? 'Survey updated' : `Draft saved as ${id}`);
      if (publishAfter) {
        const schedFuture = survey.scheduled_at && new Date(survey.scheduled_at) > new Date();
        const p = await fetch(`${API}/surveys/${id}/publish`, { method: 'POST', headers: getAuthHeader() });
        if (p.ok) {
          if (schedFuture) {
            const when = new Date(survey.scheduled_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
            toast.success(`Scheduled — this survey goes live on ${when}. Invitation emails will be sent automatically at that time.`, { duration: 6000 });
          } else {
            toast.success('Published — invitation emails are being sent to all selected recipients.', { duration: 5000 });
          }
        }
      }
      navigate('/surveys');
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const r = await fetch(`${API}/upload`, { method: 'POST', headers: getAuthHeader(), body: fd });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || 'Upload failed'); return; }
    setSurvey(s => ({ ...s, logo_url: d.url }));
    toast.success('Logo uploaded');
  };

  return (
    <Layout>
      <div className="space-y-4 max-w-5xl mx-auto" data-testid="survey-builder">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/surveys')}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">Save Draft</Button>
            {(!isEdit || ['Draft', 'Scheduled'].includes(survey.effective_status || survey.status)) && (() => {
              const schedFuture = survey.scheduled_at && new Date(survey.scheduled_at) > new Date();
              return (
                <Button onClick={() => save(true)} disabled={saving} className="text-white" style={{ backgroundColor: accent }} data-testid="publish-now-btn">
                  {schedFuture ? 'Save & Schedule' : 'Save & Publish'}
                </Button>
              );
            })()}
          </div>
        </div>

        <div className="sticky top-0 z-10 bg-background/80 backdrop-blur border-b">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map(t => {
              const Icon = t.icon; const active = tab === t.value;
              return (
                <button key={t.value} onClick={() => setTab(t.value)} data-testid={`survey-builder-tab-${t.value}`}
                  className="relative px-4 py-3 text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors"
                  style={active ? { color: accent } : { color: '#6b7280' }}>
                  <Icon className="h-4 w-4" />{t.label}
                  {active && <motion.div layoutId="builder-tab-underline" className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full" style={{ backgroundColor: accent }} />}
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2 }}>

            {tab === 'details' && (
              <Card>
                <CardHeader><CardTitle className="text-base">Survey Details</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label>Title *</Label><Input value={survey.title} onChange={e => setSurvey({ ...survey, title: e.target.value })} data-testid="survey-title" /></div>
                  <div><Label>Description</Label><Textarea value={survey.description} onChange={e => setSurvey({ ...survey, description: e.target.value })} /></div>
                  <div>
                    <Label>Form Purpose</Label>
                    <Select value={survey.purpose || 'general'} onValueChange={v => setSurvey({ ...survey, purpose: v })}>
                      <SelectTrigger data-testid="survey-purpose"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Survey (internal staff)</SelectItem>
                        <SelectItem value="cfv_feedback">Client Factory Visit — Visitor Feedback Form</SelectItem>
                      </SelectContent>
                    </Select>
                    {(survey.purpose === 'cfv_feedback') && (
                      <p className="text-[11px] mt-1 text-indigo-600 dark:text-indigo-400" data-testid="cfv-feedback-note">
                        This form is sent to <b>external Client Factory Visit visitors</b> via a private link after their final visit departure. The Audience tab does not apply — visitors don't log in, and their name/email/company are pre-filled &amp; locked.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div>
                      <Label>Visibility</Label>
                      <div className="mt-1"><Badge variant="outline" className="h-9 px-3 flex items-center w-fit" data-testid="survey-visibility">Department</Badge></div>
                      <p className="text-[11px] text-muted-foreground mt-1">Visible to your department creators.</p>
                    </div>
                    <div>
                      <Label>Scheduled At</Label>
                      <Input type="datetime-local" value={survey.scheduled_at || ''} onChange={e => setSurvey({ ...survey, scheduled_at: e.target.value })} data-testid="survey-scheduled-at" />
                      {survey.scheduled_at && new Date(survey.scheduled_at) > new Date() ? (
                        <p className="text-[11px] mt-1 flex items-start gap-1 text-amber-600 dark:text-amber-400" data-testid="schedule-signal-future">
                          <Clock className="h-3 w-3 mt-0.5 shrink-0" />
                          Will be <b>scheduled</b> — goes live &amp; invites send on {new Date(survey.scheduled_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}.
                        </p>
                      ) : (
                        <p className="text-[11px] mt-1 text-emerald-600 dark:text-emerald-400" data-testid="schedule-signal-now">Blank = publishes immediately &amp; sends invites now.</p>
                      )}
                    </div>
                    <div>
                      <Label>Send From (Department)</Label>
                      <Select value={survey.sender_department || 'Admin'} onValueChange={v => setSurvey({ ...survey, sender_department: v })}>
                        <SelectTrigger data-testid="survey-sender-department"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Travel">Travel</SelectItem>
                          <SelectItem value="Human resources & Management">Human resources &amp; Management</SelectItem>
                          <SelectItem value="Admin">Admin</SelectItem>
                          <SelectItem value="Information Technology">Information Technology</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Emails are sent from your department's mailbox (Travel → traveldesk, HR &amp; Mgmt → hrd, Admin → adminmum, IT → support). Leave Scheduled At blank to publish immediately.</p>
                  <div><Label>Thank-you Message</Label><Input value={survey.thank_you_message || ''} onChange={e => setSurvey({ ...survey, thank_you_message: e.target.value })} /></div>
                </CardContent>
              </Card>
            )}

            {tab === 'questions' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Questions</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">Drag the handle to reorder.</p>
                  </div>
                  <Select onValueChange={(t) => addQ(t)}>
                    <SelectTrigger className="h-8 text-xs w-[180px]" data-testid="add-question-type"><SelectValue placeholder="Add question…" /></SelectTrigger>
                    <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </CardHeader>
                <CardContent>
                  <Reorder.Group axis="y" values={survey.questions} onReorder={setQuestions} className="space-y-3">
                    {survey.questions.map((q, idx) => (
                      <QuestionEditor key={q.id} q={q} idx={idx} allQuestions={survey.questions}
                        onUpdate={(patch) => updateQ(q.id, patch)} onRemove={() => removeQ(q.id)} />
                    ))}
                  </Reorder.Group>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => addQ('short_text')}><Plus className="h-4 w-4 mr-1" />Add Question</Button>
                </CardContent>
              </Card>
            )}

            {tab === 'audience' && (
              <Card>
                <CardHeader><CardTitle className="text-base">Audience</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <label className="flex items-center gap-2 text-sm"><Switch data-testid="audience-entire-org-switch" checked={survey.audience.entire_org} onCheckedChange={v => setSurvey({ ...survey, audience: { ...survey.audience, entire_org: v } })} />Send to Entire Organization</label>
                  {!survey.audience.entire_org && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <MultiSelectDropdown label="Departments" testid="audience-departments" options={audienceOpts.departments} selected={survey.audience.departments} onChange={list => setAudienceList('departments', list)} />
                      <MultiSelectDropdown label="Locations" testid="audience-locations" options={audienceOpts.locations} selected={survey.audience.locations} onChange={list => setAudienceList('locations', list)} />
                      <MultiSelectDropdown label="Entities" testid="audience-entities" options={audienceOpts.entities || []} selected={survey.audience.entities} onChange={list => setAudienceList('entities', list)} />
                      <div className="md:col-span-2">
                        <EmployeeMultiSelect employees={filterEmployees(audienceOpts.employees, survey.audience)} selected={survey.audience.employee_ids} onChange={list => setAudienceList('employee_ids', list)} />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {tab === 'branding' && (
              <Card>
                <CardHeader><CardTitle className="text-base">Branding & Theme</CardTitle></CardHeader>
                <CardContent className="space-y-6">
                  <ColourRow label="Theme Colour" presets={THEME_PRESETS} value={accent} onChange={c => setSurvey({ ...survey, theme_color: c })} testid="theme" />
                  <ColourRow label="Background Colour" presets={BG_PRESETS} value={survey.background_color || '#F9FAFB'} onChange={c => setSurvey({ ...survey, background_color: c })} testid="bg" />
                  <div>
                    <Label className="text-xs font-bold tracking-[0.1em] uppercase text-muted-foreground">Logo</Label>
                    <div className="flex items-center gap-4 mt-2">
                      {survey.logo_url ? (
                        <img src={fileUrl(survey.logo_url, sessionToken)} alt="logo" className="h-16 w-16 object-contain border rounded-lg bg-muted/30" data-testid="branding-logo-preview" />
                      ) : (
                        <div className="h-16 w-16 flex items-center justify-center border-2 border-dashed rounded-lg bg-muted/20"><Upload className="h-5 w-5 text-muted-foreground" /></div>
                      )}
                      <LogoUploadBtn onUpload={uploadLogo} />
                      {survey.logo_url && <Button variant="ghost" size="sm" onClick={() => setSurvey({ ...survey, logo_url: '' })}>Remove</Button>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {tab === 'preview' && (
              <div data-testid="survey-preview">
                <InteractivePreview survey={survey} accent={accent} getAuthHeader={getAuthHeader} sessionToken={sessionToken} />
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>
    </Layout>
  );
}

function QuestionEditor({ q, idx, allQuestions, onUpdate, onRemove }) {
  const controls = useDragControls();
  // Local raw text for comma-separated inputs (avoids split/join round-trip space multiplication)
  const [optionsText, setOptionsText] = useState((q.options || []).join(', '));
  const [rowsText, setRowsText] = useState((q.rows || []).join(', '));
  useEffect(() => { setOptionsText((q.options || []).join(', ')); }, [q.id, q.type]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRowsText((q.rows || []).join(', ')); }, [q.id, q.type]); // eslint-disable-line react-hooks/exhaustive-deps
  const priorChoiceQs = allQuestions.slice(0, idx).filter(x => CHOICE_TYPES.has(x.type) && x.label);

  return (
    <Reorder.Item value={q} dragListener={false} dragControls={controls}
      className="border rounded-md p-3 space-y-2 bg-white" data-testid={`question-${idx}`}>
      <div className="flex justify-between gap-2">
        <div className="pt-2 cursor-grab active:cursor-grabbing touch-none" onPointerDown={(e) => controls.start(e)} data-testid={`question-${idx}-drag`}>
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <Input value={q.label} onChange={e => onUpdate({ label: e.target.value })} placeholder={q.type === 'section' ? 'Section title' : 'Question label'} />
            <Select value={q.type} onValueChange={v => onUpdate({ type: v, options: NEEDS_OPTIONS.has(v) ? (q.options || ['Option 1', 'Option 2']) : undefined, rows: NEEDS_ROWS.has(v) ? (q.rows || ['Row 1', 'Row 2']) : undefined })}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {q.type !== 'section' && (
            <label className="flex items-center gap-2 text-xs"><Checkbox checked={q.required} onCheckedChange={v => onUpdate({ required: !!v })} />Required</label>
          )}
          {NEEDS_OPTIONS.has(q.type) && (
            <div className="space-y-1">
              <Label className="text-xs">{NEEDS_ROWS.has(q.type) ? 'Columns / Scale (comma-separated)' : 'Options (comma-separated)'}</Label>
              <Input data-testid={`question-${idx}-options`} value={optionsText} onChange={e => { setOptionsText(e.target.value); onUpdate({ options: e.target.value.split(',') }); }} />
            </div>
          )}
          {NEEDS_ROWS.has(q.type) && (
            <div className="space-y-1">
              <Label className="text-xs">Rows / Statements (comma-separated)</Label>
              <Input data-testid={`question-${idx}-rows`} placeholder="Row 1, Row 2, Row 3" value={rowsText} onChange={e => { setRowsText(e.target.value); onUpdate({ rows: e.target.value.split(',') }); }} />
            </div>
          )}

          {/* Conditional logic (supports multiple conditions with AND/OR) */}
          {q.type !== 'section' && (
            <ConditionsEditor q={q} idx={idx} priorChoiceQs={priorChoiceQs} allQuestions={allQuestions} onUpdate={onUpdate} />
          )}

          {CHOICE_TYPES.has(q.type) && <SpecifyConfig q={q} onUpdate={onUpdate} />}
        </div>
        <Button variant="ghost" size="icon" onClick={onRemove}><Trash2 className="h-4 w-4 text-destructive" /></Button>
      </div>
    </Reorder.Item>
  );
}

const condValues = (c) => (Array.isArray(c.values) && c.values.length ? c.values : (c.value ? [c.value] : []));

// Multi-select of answer values for one condition
function ConditionValuesPicker({ options, selected, disabled, onChange, testId }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const label = selected.length === 0 ? 'Value(s)…' : selected.length <= 2 ? selected.join(', ') : `${selected.length} selected`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className="h-8 min-w-[150px] max-w-[240px] justify-between text-xs font-normal" data-testid={testId}>
          <span className="truncate">{label}</span><ChevronDown className="h-3 w-3 ml-1 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>No options.</CommandEmpty>
            <CommandGroup>
              {options.map(v => (
                <CommandItem key={v} value={v} onSelect={() => toggle(v)} data-testid={`${testId}-opt-${v}`}>
                  <Checkbox checked={selected.includes(v)} className="mr-2 h-3.5 w-3.5" /> {v}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Multiple conditional-logic conditions combined with AND/OR (with legacy single-condition migration)
function ConditionsEditor({ q, idx, priorChoiceQs, allQuestions, onUpdate }) {
  const conds = (q.conditions && q.conditions.length)
    ? q.conditions
    : (q.condition?.question_id ? [q.condition] : []);
  const [open, setOpen] = useState(conds.length > 0);
  const logic = q.condition_logic || 'AND';

  const commit = (list, newLogic) => onUpdate({ conditions: list, condition_logic: newLogic ?? logic, condition: null });
  const addCond = () => commit([...conds, { question_id: '', operator: 'equals', value: '' }]);
  const updateCond = (i, patch) => commit(conds.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const clearAll = () => { onUpdate({ conditions: null, condition: null, condition_logic: null }); setOpen(false); };
  const removeCond = (i) => {
    const list = conds.filter((_, j) => j !== i);
    if (list.length === 0) clearAll(); else commit(list);
  };
  const valuesFor = (qid) => {
    const rq = allQuestions.find(x => x.id === qid);
    return rq ? (rq.type === 'yes_no' ? ['Yes', 'No'] : (rq.options || []).filter(Boolean)) : [];
  };

  if (!open) {
    if (priorChoiceQs.length === 0) return null;
    return (
      <div className="pt-1">
        <button type="button" className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground"
          onClick={() => { setOpen(true); if (conds.length === 0) addCond(); }} data-testid={`question-${idx}-add-condition`}>
          <GitBranch className="h-3 w-3" /> Add condition
        </button>
      </div>
    );
  }

  return (
    <div className="pt-1">
      <div className="rounded-md border bg-muted/30 p-2 space-y-2" data-testid={`question-${idx}-condition`}>
        {priorChoiceQs.length === 0 ? (
          <p className="text-xs text-muted-foreground">Add a choice question above this one to use conditions.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium">Show only if</span>
              {conds.length > 1 && (
                <Select value={logic} onValueChange={v => commit(conds, v)}>
                  <SelectTrigger className="h-7 w-[180px]" data-testid={`question-${idx}-condition-logic`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">ALL match (AND)</SelectItem>
                    <SelectItem value="OR">ANY matches (OR)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {conds.map((c, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`question-${idx}-condition-row-${i}`}>
                {i > 0 && <span className="text-muted-foreground w-8 font-semibold">{logic === 'OR' ? 'OR' : 'AND'}</span>}
                <Select value={c.question_id || ''} onValueChange={v => updateCond(i, { question_id: v, value: '', values: [] })}>
                  <SelectTrigger className="h-8 w-[170px]" data-testid={`question-${idx}-condition-q-${i}`}><SelectValue placeholder="Question…" /></SelectTrigger>
                  <SelectContent>{priorChoiceQs.map(pq => <SelectItem key={pq.id} value={pq.id}>{pq.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={c.operator || 'equals'} onValueChange={v => updateCond(i, { operator: v })}>
                  <SelectTrigger className="h-8 w-[120px]" data-testid={`question-${idx}-condition-op-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="equals">is any of</SelectItem><SelectItem value="not_equals">is none of</SelectItem></SelectContent>
                </Select>
                <ConditionValuesPicker
                  options={valuesFor(c.question_id)} selected={condValues(c)} disabled={!c.question_id}
                  onChange={vals => updateCond(i, { values: vals, value: vals[0] || '' })}
                  testId={`question-${idx}-condition-value-${i}`} />
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeCond(i)} data-testid={`question-${idx}-condition-remove-${i}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addCond} data-testid={`question-${idx}-condition-add-more`}>
                <Plus className="h-3 w-3 mr-1" /> Add condition
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearAll}>Clear all</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Per-option "specify" follow-up config (Yes/No, single/multi choice, dropdown)
function SpecifyConfig({ q, onUpdate }) {
  const opts = q.type === 'yes_no' ? ['Yes', 'No'] : (q.options || []).map(o => o.trim()).filter(Boolean);
  const cfg = q.option_config || {};
  const setOptCfg = (opt, patch) => {
    const cur = q.option_config || {};
    onUpdate({ option_config: { ...cur, [opt]: { ...(cur[opt] || {}), ...patch } } });
  };
  if (!opts.length) return null;
  return (
    <div className="rounded-md border bg-muted/20 p-2 space-y-2" data-testid={`specify-config-${q.id}`}>
      <p className="text-xs font-medium flex items-center gap-1 text-muted-foreground">Follow-up "specify" box per option</p>
      {opts.map(opt => {
        const oc = cfg[opt] || {};
        return (
          <div key={opt} className="space-y-1.5 border-t pt-1.5 first:border-t-0 first:pt-0">
            <label className="flex items-center justify-between gap-2 text-xs">
              <span className="font-medium truncate">{opt}</span>
              <span className="flex items-center gap-1.5 shrink-0">
                <span className="text-muted-foreground">Add specify box</span>
                <Switch checked={!!oc.specify} onCheckedChange={v => setOptCfg(opt, { specify: !!v })} data-testid={`specify-toggle-${q.id}-${opt}`} />
              </span>
            </label>
            {oc.specify && (
              <div className="flex flex-col sm:flex-row gap-2 pl-1">
                <Input className="h-8 text-xs flex-1" placeholder='Prompt e.g. "Please tell us why"'
                  value={oc.prompt || ''} onChange={e => setOptCfg(opt, { prompt: e.target.value })} data-testid={`specify-prompt-${q.id}-${opt}`} />
                <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                  <Checkbox checked={!!oc.required} onCheckedChange={v => setOptCfg(opt, { required: !!v })} data-testid={`specify-required-${q.id}-${opt}`} />
                  Mandatory
                </label>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LogoUploadBtn({ onUpload }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); try { await onUpload(f); } finally { setBusy(false); } };
  return (
    <label className="cursor-pointer">
      <span className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-muted/50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
      </span>
      <input type="file" accept="image/*" className="hidden" onChange={handle} disabled={busy} data-testid="logo-upload" />
    </label>
  );
}

function ColourRow({ label, presets, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs font-bold tracking-[0.1em] uppercase text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        {presets.map(c => (
          <button key={c} type="button" onClick={() => onChange(c)} data-testid={`${testid}-swatch-${c.replace('#', '')}`}
            className="h-8 w-8 rounded-full border-2 transition-transform hover:scale-110"
            style={{ backgroundColor: c, borderColor: (value || '').toLowerCase() === c.toLowerCase() ? '#111' : '#e5e7eb' }} />
        ))}
        <input type="color" value={value} onChange={e => onChange(e.target.value)} data-testid={`${testid}-color-picker`} className="h-8 w-12 rounded cursor-pointer border" />
        <span className="text-sm font-mono text-muted-foreground">{value}</span>
      </div>
    </div>
  );
}

export function MultiSelectDropdown({ label, options, selected, onChange, testid }) {
  const toggle = (o) => onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o]);
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between font-normal" data-testid={`${testid}-trigger`}>
            <span className="truncate">{selected.length ? `${selected.length} selected` : `Select ${label.toLowerCase()}…`}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="min-w-[16rem] w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
            <CommandList>
              <CommandEmpty>No results.</CommandEmpty>
              <CommandGroup>
                {options.map(o => (
                  <CommandItem key={o} value={o} onSelect={() => toggle(o)} data-testid={`${testid}-option-${o}`}>
                    <Checkbox checked={selected.includes(o)} className="mr-2" />{o}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {selected.map(o => <Badge key={o} variant="secondary" className="cursor-pointer" onClick={() => toggle(o)}>{o} ×</Badge>)}
        </div>
      )}
    </div>
  );
}

export function EmployeeMultiSelect({ employees, selected, onChange }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const byId = Object.fromEntries(employees.map(e => [e.employee_id, e]));
  return (
    <div className="space-y-1">
      <Label className="text-xs">Individual Employees</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between font-normal" data-testid="audience-employees-trigger">
            <span className="truncate">{selected.length ? `${selected.length} selected` : 'Select employees…'}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="min-w-[16rem] w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search by name or email…" />
            <CommandList>
              <CommandEmpty>No employees found.</CommandEmpty>
              <CommandGroup>
                {employees.map(e => (
                  <CommandItem key={e.employee_id} value={`${e.full_name} ${e.email}`} onSelect={() => toggle(e.employee_id)} data-testid={`audience-employee-${e.employee_id}`}>
                    <Checkbox checked={selected.includes(e.employee_id)} className="mr-2" />
                    <span className="flex-1">{e.full_name} <span className="text-xs text-muted-foreground">· {e.department}</span></span>
                    <span className="text-xs text-muted-foreground">{e.email}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {selected.map(id => <Badge key={id} variant="secondary" className="cursor-pointer" onClick={() => toggle(id)}>{byId[id]?.full_name || id} ×</Badge>)}
        </div>
      )}
    </div>
  );
}

// Interactive, fillable preview (no submission)
function InteractivePreview({ survey, accent, getAuthHeader, sessionToken }) {
  const [answers, setAnswers] = useState({});
  const [specify, setSpecify] = useState({});
  const setA = (qid, v) => setAnswers(a => ({ ...a, [qid]: v }));
  const setSpec = (qid, opt, text) => setSpecify(s => ({ ...s, [qid]: { ...(s[qid] || {}), [opt]: text } }));
  const bg = survey.background_color || '#F9FAFB';
  const logoSrc = fileUrl(survey.logo_url, sessionToken);
  let n = 0;
  return (
    <div style={{ backgroundColor: bg }} className="rounded-2xl p-4 sm:p-6">
      <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden border bg-white shadow-sm">
        <div className="h-2 w-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
        <div className="p-6 space-y-4">
          {logoSrc && <img src={logoSrc} alt="logo" className="h-16 object-contain mx-auto block" data-testid="preview-logo" />}
          <h2 className="text-2xl font-bold tracking-tight">{survey.title || 'Untitled survey'}</h2>
          {survey.description && <p className="text-sm text-muted-foreground">{survey.description}</p>}
          {(survey.questions || []).map(q => {
            if (q.type === 'section') return <h3 key={q.id} className="text-lg font-semibold border-b pb-1 pt-2">{q.label || 'Section'}</h3>;
            if (!isVisible(q, answers)) return null;
            n += 1;
            return (
              <div key={q.id} className="rounded-xl border p-4" data-testid={`preview-question-${q.id}`}>
                <p className="text-sm font-medium">{n}. {q.label || '(untitled)'} {q.required && <span style={{ color: accent }}>*</span>}</p>
                <div className="mt-2"><QuestionField q={q} value={answers[q.id]} onChange={v => setA(q.id, v)} getAuthHeader={getAuthHeader} accent={accent} />
                  <SpecifyInputs q={q} value={answers[q.id]} specify={specify[q.id]} onChange={(opt, text) => setSpec(q.id, opt, text)} accent={accent} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
